import ImmutableModel, {
  asString,
  asInstance,
  asBoolean,
  asModel,
  asNullOr,
  asFloat,
  asPlainObject,
  asArray,
  withDefault
} from '@aytm/lib/immutable/ImmutableModel'
import ItemsOrderedMap from '@aytm/lib/immutable/ItemsOrderedMap'
import ListModel from '@aytm/lib/immutable/ListModel'

import { BLOCK_TYPES, BLOCK_SUBJECT_TYPES } from '@aytm/app/insights/dashboard/lib/config'
import { selectFilters } from '@aytm/app/insights/dashboard/lib/filters'
import { asRequiredUID } from '@aytm/app/insights/dashboard/lib/immutable'

import ColorComplex from '@aytm/lib/rfd-ui/components/advanced/color-complex'

export { ReservedSectionNames } from './BlockModel.elements'
import { GroupModel, MetricsOrderedMap, MethodListModel, MilestoneModel } from './BlockModel.models'
export { MethodListModel, FilterItemModel, MetricsOrderedMap }

export const FilterValuesListModel = ListModel(asString, 'FilterValuesListModel')
export const getEmptyFilterValuesListModel = () => FilterValuesListModel()

class FilterItemModel extends ImmutableModel({
  uid: asString,
  // @ts-ignore
  values: asInstance(FilterValuesListModel.isFilterValuesListModel, FilterValuesListModel)
}) {}
ImmutableModel.decorator({ name: 'FilterItemModel' })(FilterItemModel)

export class ItemModel extends ImmutableModel({
  uid: asString,
  pinned: asBoolean,
  letter: asNullOr(asString),
  waveUid: asNullOr(asString)
}) {
  getObj() {
    return this.toJS()
  }
}
ImmutableModel.decorator({ name: 'ItemModel' })(ItemModel)

export class ColorModel extends ImmutableModel({
  background: asNullOr(asString),
  text: asNullOr(asString)
}) {}
ImmutableModel.decorator({ name: 'ColorModel' })(ColorModel)

export class ColorsModel extends ImmutableModel({
  upper: asNullOr(asModel(ColorModel)),
  lower: asNullOr(asModel(ColorModel))
}) {}
ImmutableModel.decorator({ name: 'ColorsModel' })(ColorsModel)

export const FiltersOrderedMap = ItemsOrderedMap(
  // @ts-ignore
  x => (FilterItemModel.isFilterItemModel(x) ? x : FilterItemModel.of(x)),
  null,
  'FiltersOrderedMap'
)

export const getEmptyFiltersOrderedMap = () => FiltersOrderedMap()

export const ItemListModel = ListModel(asModel(ItemModel), 'ItemListModel')
export const GroupListModel = ListModel(asModel(GroupModel), 'GroupListModel')
export const FilterListModel = ListModel(asModel(FilterItemModel), 'FilterListModel')
export const AttributesListModel = ListModel(asString, 'AttributesListModel')
export const MilestoneListModel = ListModel(asModel(MilestoneModel), 'MilestoneListModel')

export class TrackAttributesModel extends ImmutableModel({
  attribute: asNullOr(asString),
  entity: asNullOr(asString)
}) {}
ImmutableModel.decorator({ name: 'TrackAttributesModel' })(TrackAttributesModel)

// @ts-ignore
const defaultColors = ColorsModel.of(ColorComplex.palette[0])

class BlockModel extends ImmutableModel({
  version: asNullOr(asString),
  uid: asRequiredUID(),
  chartAttribute: asNullOr(asString),
  chartMethod: asNullOr(asString),
  colors: withDefault(defaultColors, asModel(ColorsModel)),
  // @ts-ignore
  dataAttributes: asNullOr(asInstance(AttributesListModel.isAttributesListModel, AttributesListModel)),
  // @ts-ignore
  entityAttributes: asNullOr(asInstance(AttributesListModel.isAttributesListModel, AttributesListModel)),
  // @ts-ignore
  filters: asInstance(FiltersOrderedMap.isFiltersOrderedMap, FiltersOrderedMap),
  // @ts-ignore
  items: asNullOr(asInstance(ItemListModel.isItemListModel, ItemListModel)),
  hideBlankColumns: withDefault(false, asBoolean),
  // @ts-ignore
  metaAttributes: asNullOr(asInstance(AttributesListModel.isAttributesListModel, AttributesListModel)),
  // @ts-ignore
  attributeMetrics: asInstance(MetricsOrderedMap.isMetricsOrderedMap, MetricsOrderedMap),
  purpose: withDefault('portal', asString),
  createdAt: asNullOr(asString),
  createdBy: asNullOr(asString),
  updatedAt: asNullOr(asString),
  updatedBy: asNullOr(asString),
  sortOrder: asNullOr(asString),
  confidenceLevel: withDefault(0.95, asFloat),
  secondaryConfidenceLevel: withDefault(0.9, asFloat),
  fullScreen: withDefault(false, asBoolean),
  selectedAttribute: asNullOr(asString),
  currentPreset: asNullOr(asString),
  showImages: asBoolean, //withDefault(true, asBoolean),
  showRanks: asBoolean, //withDefault(true, asBoolean),
  showData: withDefault(true, asBoolean),
  showMetadata: withDefault(true, asBoolean),
  showSigtest: withDefault(true, asBoolean),
  showRowAverages: withDefault(false, asBoolean),
  filteredRowAverages: withDefault(false, asBoolean),
  showChart: withDefault(false, asBoolean),
  sortByAttribute: asNullOr(asString),
  sortByMethod: asNullOr(asString),
  sortOrderManualEnabled: withDefault(false, asBoolean),
  sortOrderPositions: asNullOr(asArray),
  showTimeline: withDefault(false, asBoolean),
  timelineZoom: withDefault(1, asFloat),
  timelineOffset: withDefault(0, asFloat),
  title: asNullOr(withDefault('Insights', asString)),
  type: withDefault(BLOCK_TYPES.NONE, asString),
  swap: withDefault(false, asBoolean),
  dataChartScale: withDefault('min-max', asString),
  dataChartType: withDefault('line', asString),
  blockChartScale: withDefault('min-max', asString),
  blockChartType: withDefault('column', asString),
  currentWave: asNullOr(asString),
  currentSplit: asNullOr(asString),
  trackTarget: asNullOr(withDefault('entity', asString)),
  trackAttributes: asNullOr(asModel(TrackAttributesModel)),
  // @ts-ignore
  sections: asNullOr(asInstance(GroupListModel.isGroupListModel, GroupListModel)),
  // @ts-ignore
  milestones: asNullOr(asInstance(MilestoneListModel.isMilestoneListModel, MilestoneListModel)),
  letterStore: withDefault({}, asPlainObject)
}) {
  static blockType = BLOCK_TYPES.NONE
  static title = 'Insights'
  getObject(key, fallback) {
    return this.get(key) || fallback
  }
  getArray(key) {
    return this.get(key)?.toArray() || []
  }
  getJS(key, fallback) {
    return this.get(key)?.toJS() || fallback
  }
  getFilters(mask) {
    return selectFilters(this.get('filters'), mask)
  }
  getItemsArray() {
    const items = this.get('items')
      ?.toArray()
      .map(item => item.getObj())
    return items || []
  }
  getBlockItems() {
    return this.get('items') || null
  }
  getSubjectType() {
    return BLOCK_SUBJECT_TYPES[this.get('type')] || null
  }
  findItemLetter(uid) {
    const found = this.get('items')?.find(v => uid === v.get('uid'))
    return found?.get('letter') || null
  }
  hasItemLetter(letter) {
    const found = this.get('items')?.find(v => letter === v.get('letter'))
    return found || false
  }
  getSections() {
    const sections = this.get('sections')
    return sections
      ? sections
      : GroupListModel().push(
          {
            name: 'Attributes',
            target: 'attribute',
            items: this.get('dataAttributes') || [],
            metrics: this.get('attributeMetrics') || [],
            disabled: [],
            collapsed: false
          },
          {
            name: 'Entities',
            target: 'entity',
            items: this.get('entityAttributes') || [],
            metrics: [],
            disabled: [],
            collapsed: false
          },
          {
            name: 'Metadata',
            target: 'meta',
            items: this.get('metaAttributes') || [],
            metrics: [],
            disabled: [],
            collapsed: false
          }
        )
  }
}
ImmutableModel.decorator({ name: 'BlockModel' })(BlockModel)
export default BlockModel

// @ts-ignore
export const getEmptyBlockModel = () => BlockModel.empty()
